import { mapBricksLocaleToNextJsLocale, mapStrapiLocaleToNextJsLocale } from "@utils";
import { useCallback, useEffect, useState } from "react";
import { types, useAdminContext } from "react-bricks/frontend";

/**
 * Invalidates the cache
 */
export const cacheInvalidation = () => ({
  name: "cache-invalidation",
  label: "",
  type: types.SideEditPropType.Custom,
  component: () => {
    const context = useAdminContext();
    const [fetching, setFetching] = useState(false);
    const handleClick = async () => {
      setFetching(true);
      const token = window.localStorage.getItem("rb_t");
      const response = await fetch("/api/clear-cache", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pageId: context.currentPage.pageId,
        }),
      });

      setFetching(false);

      const { slug } = await response.json();

      const lang = mapBricksLocaleToNextJsLocale(context?.currentPage?.language);
      const url = `/${lang}/${slug}?preview=${+new Date()}`;
      // allow the cache to clear
      window.open(url, slug);
    };

    // only contains the page ID (context.id)
    return (
      <button
        onClick={handleClick}
        type="button"
        className="text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors duration-150 focus:outline-none"
        disabled={fetching}
      >
        {fetching ? "Invalidating cache..." : "Invalidate cache & open"}
      </button>
    );
  },
});

/**
 * For english pages, lets the user select what locales we want the page to apply to
 */
export const enabledForCountries = () => ({
  name: "enabledForCountries",
  type: types.SideEditPropType.Custom,
  label: "Use for countries:",
  component: ({ onChange, value }) => {
    const { currentPage } = useAdminContext();
    const [selected, setSelected] = useState<string[]>(value);
    const localeOptions = ["uk", "us", "za"];

    useEffect(() => {
      if (!value) {
        const defaults = ["uk"];
        setSelected(defaults);
        onChange(defaults);
      }
    }, [setSelected, onChange, value]);

    const handleToggle = (locale: string) => {
      setSelected((prev = []) => {
        const newSelected = prev.includes(locale)
          ? prev.filter((item) => item !== locale)
          : [...prev, locale];

        onChange(newSelected);
        return newSelected;
      });
    };

    if (currentPage.language && currentPage.language !== "en") {
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        {localeOptions.map((locale) => (
          <label key={locale} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={selected?.includes(locale)}
              onChange={() => handleToggle(locale)}
              className="form-checkbox h-4 w-4 text-blue-600"
            />
            <span className="text-sm">{locale.toUpperCase()}</span>
          </label>
        ))}
      </div>
    );
  },
});

export const hideHeader = () => ({
  name: "hideHeader",
  label: "Hide Header?",
  type: types.SideEditPropType.Boolean,
});

export const hideFooter = () => ({
  name: "hideFooter",
  label: "Hide Footer?",
  type: types.SideEditPropType.Boolean,
});
